import { type RefObject, useCallback, useEffect, useState } from "react";

export function useScrollProgress(ref: RefObject<HTMLDivElement>) {
	const [scrollData, setScrollData] = useState<{
		progress: number;
		scrollTop: number;
		isAtBottom: boolean;
		isAtTop: boolean;
		scrollDirection: "up" | "down" | null;
	}>({
		progress: 0,
		scrollTop: 0,
		isAtBottom: false,
		isAtTop: true,
		scrollDirection: null, // 'up' | 'down' | null
	});

	const handleScroll = useCallback(() => {
		const element = ref.current;
		if (!element) return;

		const { scrollTop, scrollHeight, clientHeight } = element;

		const maxScroll = scrollHeight - clientHeight;
		const progress = maxScroll > 0 ? (scrollTop / maxScroll) * 100 : 0;

		const direction = scrollTop > scrollData.scrollTop ? "down" : "up";

		setScrollData((prev) => {
			if (Math.abs(prev.scrollTop - scrollTop) < 1 && prev.scrollDirection === direction) {
				return prev;
			}

			return {
				progress: Math.round(Math.min(Math.max(progress, 0), 100)), // Clamp between 0-100
				scrollTop,
				isAtBottom: Math.abs(scrollHeight - clientHeight - scrollTop) < 1, // < 1 handles sub-pixel rendering issues
				isAtTop: scrollTop === 0,
				scrollDirection: direction,
			};
		});
	}, [ref, scrollData.scrollTop]);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;
		let ticking = false;

		function onScroll() {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					handleScroll();
					ticking = false;
				});
				ticking = true;
			}
		}

		element.addEventListener("scroll", onScroll);
		window.addEventListener("resize", onScroll);
		handleScroll();

		return () => {
			element.removeEventListener("scroll", onScroll);
			window.removeEventListener("resize", onScroll);
		};
	}, [ref, handleScroll]);

	return scrollData;
}
